/* ============================================================
   CUSTOM-MEGA-MENU-HOVER.JS – Dynamische Produktanzeige im Mega-Menü
   Erstellt: 2026-02-27

   Zeigt beim Hover über Untermenü-Links 3 verfügbare Produkte
   aus der jeweiligen Collection an.
   ============================================================ */

(function () {
  'use strict';

  let productCache = {}; // Cache für geladene Produkte
  let currentHoverLink = null;
  let hoverTimeout = null;

  /**
   * Extrahiert den Collection-Handle aus einer URL
   * z.B. "/de/collections/hoodies" → "hoodies"
   */
  function getCollectionHandle(url) {
    if (!url) return null;
    const match = url.match(/\/collections\/([^/?]+)/);
    return match ? match[1] : null;
  }

  /**
   * Formatiert Preis zu lokalem Währungsformat
   * Die Collection JSON API gibt Preise bereits in der Basis-Währung zurück (z.B. 59.00 EUR)
   */
  function formatMoney(amount) {
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      const locale = document.documentElement.lang || 'de-DE';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      // Fallback für ältere Browser
      return amount.toFixed(2).replace('.', ',') + ' €';
    }
  }

  /**
   * Ermittelt den Locale-Prefix aus der aktuellen URL
   * z.B. "/de/products/..." → "/de"
   */
  function getLocalePrefix() {
    const match = window.location.pathname.match(/^\/([a-z]{2})(?:\/|$)/);
    return match ? `/${match[1]}` : '';
  }

  /**
   * Lädt Produkte einer Collection via Shopify JSON API
   * Gibt die ersten 3 verfügbaren (nicht ausverkauften) Produkte zurück
   */
  async function fetchCollectionProducts(handle) {
    // Prüfe Cache
    if (productCache[handle]) {
      return productCache[handle];
    }

    try {
      const response = await fetch(`/collections/${handle}/products.json?limit=50`);
      if (!response.ok) throw new Error('Collection not found');

      const data = await response.json();

      // Filtere verfügbare Produkte (mindestens 1 Variante auf Lager)
      const availableProducts = data.products.filter(product => {
        return product.variants && product.variants.some(v => v.available);
      });

      // Nimm die ersten 3
      const products = availableProducts.slice(0, 3);

      // Cache speichern
      productCache[handle] = products;

      return products;
    } catch (error) {
      console.warn('Fehler beim Laden der Collection:', handle, error);
      return [];
    }
  }

  /**
   * Erstellt HTML für eine Produktkarte
   */
  function createProductCard(product, index, maxProducts) {
    const variant = product.variants[0];

    // Preise sind Strings in der API (z.B. "59.00") - konvertiere zu Float
    const priceAmount = parseFloat(variant.price) || 0;
    const comparePriceAmount = variant.compare_at_price ? parseFloat(variant.compare_at_price) : 0;

    // Formatiere Preise korrekt
    const price = formatMoney(priceAmount);
    const comparePrice = comparePriceAmount > priceAmount
      ? formatMoney(comparePriceAmount)
      : null;

    // Bild-URL (mit Fallback)
    const imageUrl = product.images && product.images[0]
      ? product.images[0].src
      : product.featured_image;

    // Produkt-URL aus Handle bauen (API gibt kein url-Feld zurück!)
    const localePrefix = getLocalePrefix();
    const productUrl = `${localePrefix}/products/${product.handle}`;

    // Bestimme ob das Item auf Tablet versteckt werden soll
    const hiddenTabletClass = index >= 2 ? ' mega-menu__content-list-item--hidden-tablet' : '';

    return `
      <li class="mega-menu__content-list-item${hiddenTabletClass}">
        <product-card-link data-product-id="${product.id}" data-featured-media-url="${imageUrl}" data-product-transition="true">
          <div class="resource-card" data-resource-type="product">
            <a class="resource-card__link" href="${productUrl}">
              <span class="visually-hidden">${product.title}</span>
            </a>
            <div class="resource-card__media" style="--resource-card-aspect-ratio: 0.75;">
              <img
                src="${imageUrl}"
                alt="${product.title}"
                width="3"
                height="4"
                class="resource-card__image"
                style="object-position: 50.0% 50.0%;">
            </div>
            <div class="resource-card__content">
              <p class="resource-card__title paragraph">${product.title}</p>
              <div>
                ${comparePrice ? `<span class="price price--on-sale"><s class="price__compare">${comparePrice}</s></span>` : ''}
                <span class="price">${price}</span>
              </div>
            </div>
          </div>
        </product-card-link>
      </li>
    `;
  }

  /**
   * Aktualisiert die Produktkarten-Anzeige
   */
  async function updateProductDisplay(link) {
    const handle = getCollectionHandle(link.href);
    if (!handle) return;

    // Finde den zugehörigen Submenu-Container
    const submenu = link.closest('.menu-list__submenu-inner');
    if (!submenu) return;

    // Finde den Produktkarten-Container
    const productList = submenu.querySelector('.mega-menu__content-list--products');
    if (!productList) return;

    // Lade Produkte
    const products = await fetchCollectionProducts(handle);

    if (products.length === 0) {
      // Keine Produkte gefunden - zeige Platzhalter oder verstecke Container
      productList.innerHTML = `
        <li class="mega-menu__content-list-item">
          <div style="padding: 20px; text-align: center; color: #999; font-size: 13px;">
            Keine verfügbaren Produkte
          </div>
        </li>
      `;
      return;
    }

    // Erstelle HTML für alle Produktkarten
    const cardsHTML = products.map((product, index) =>
      createProductCard(product, index, products.length)
    ).join('');

    // Aktualisiere den Container mit smooth transition
    productList.style.opacity = '0.5';
    setTimeout(() => {
      productList.innerHTML = cardsHTML;
      productList.style.opacity = '1';
    }, 150);
  }

  /**
   * Event-Handler für Hover über Untermenü-Links
   */
  function handleLinkHover(event) {
    const link = event.currentTarget;

    // Prüfe ob es ein Collection-Link ist
    const handle = getCollectionHandle(link.href);
    if (!handle) return;

    // Vermeide mehrfache Updates für den gleichen Link
    if (currentHoverLink === link) return;

    currentHoverLink = link;

    // Verzögerung vor dem Laden (verhindert zu viele Requests bei schnellem Hovern)
    clearTimeout(hoverTimeout);
    hoverTimeout = setTimeout(() => {
      if (currentHoverLink === link) {
        updateProductDisplay(link);
      }
    }, 200);
  }

  /**
   * Lädt alle Collection-Daten im Hintergrund vor,
   * damit beim Hover sofort auf den Cache zugegriffen werden kann.
   * Nutzt requestIdleCallback (wenn verfügbar) um nicht mit
   * kritischen Ressourcen (z.B. Hero-Video) zu konkurrieren.
   * Requests werden gestaffelt (300ms Abstand) um Bandwidth zu schonen.
   */
  function prefetchAllCollections() {
    const links = document.querySelectorAll('.mega-menu__link:not(.mega-menu__link--parent)');
    const handles = new Set();

    links.forEach(function(link) {
      const handle = getCollectionHandle(link.href);
      if (handle) handles.add(handle);
    });

    let delay = 0;
    handles.forEach(function(handle) {
      setTimeout(function() {
        fetchCollectionProducts(handle);
      }, delay);
      delay += 300; // gestaffelt: je Collection 300ms versetzt
    });
  }

  /**
   * Initialisierung
   */
  function init() {
    // Finde alle Untermenü-Links im Mega-Menü
    const megaMenuLinks = document.querySelectorAll('.mega-menu__link');

    megaMenuLinks.forEach(link => {
      // Nur Links zu Collections (nicht Parent-Links)
      if (!link.classList.contains('mega-menu__link--parent')) {
        link.addEventListener('mouseenter', handleLinkHover);

        // Cursor-Style via CSS-Class
        link.classList.add('mega-menu__link--hoverable');
      }
    });

    // Produktdaten im Hintergrund vorausladen (wenn Browser idle ist)
    const schedule = window.requestIdleCallback
      ? function(fn) { window.requestIdleCallback(fn, { timeout: 3000 }); }
      : function(fn) { setTimeout(fn, 2000); };
    schedule(prefetchAllCollections);
  }

  // Init bei DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init bei Shopify Section Reload (für Theme Editor)
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector('.mega-menu')) {
      setTimeout(init, 100);
    }
  });

})();
